<template>
  <div class="import-monitor-container">
    <!-- Loading Overlay -->
    <div v-if="loading" class="loading-overlay">
      <div class="loader-box">
        <div class="loader-30"></div>
        <h6 class="loading-text">Uploading...</h6>
      </div>
    </div>
    <div class="import-monitor-body">
      <!-- Upload Illustration and File Upload -->
      <div class="upload-illustration-section text-center">
        <img src="@/assets/icons/import-modal-image.png" alt="Upload" class="import-illustration" />
        <div class="import-upload-text mt-3 mb-2">
          <div class="import-upload-title">Start by uploading a file</div>
          <div class="import-upload-desc">Choose your CSV/Excel File and Upload Now</div>
        </div>
        <input 
          type="file" 
          id="csv-file-input" 
          accept=".csv,.xls,.xlsx" 
          ref="csvFile"
          @change="validateCsvFile"
          class="file-input"
        />
      </div>
      <button type="button" class="upload-now-btn" @click="selectedFileName ? uploadCsv() : triggerFileInput()">
          <!-- Import file icon -->
          <svg v-if="!selectedFileName" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="upload-btn-icon">
            <path d="M11.6667 1.66667H5.00001C4.55798 1.66667 4.13406 1.84226 3.82151 2.1548C3.50894 2.46735 3.33334 2.89131 3.33334 3.33334V16.6667C3.33334 17.1087 3.50894 17.5326 3.82151 17.8452C4.13406 18.1577 4.55798 18.3333 5.00001 18.3333H15C15.442 18.3333 15.866 18.1577 16.1785 17.8452C16.4911 17.5326 16.6667 17.1087 16.6667 16.6667V6.66667L11.6667 1.66667Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M11.6667 1.66667V6.66667H16.6667" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10 14.1667V9.16667" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M7.5 11.6667L10 9.16667L12.5 11.6667" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <!-- Upload now icon -->
          <svg v-else width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="upload-btn-icon">
            <path d="M13.3333 13.3333L10 10L6.66667 13.3333" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10 10V17.5" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M16.9917 15.325C17.8044 14.8818 18.4465 14.1806 18.8165 13.3321C19.1866 12.4835 19.2635 11.5359 19.0351 10.6388C18.8068 9.7417 18.2862 8.94616 17.5556 8.37778C16.8249 7.80939 15.9257 7.50052 15 7.5H13.95C13.6977 6.52427 13.2276 5.61852 12.5749 4.85073C11.9222 4.08295 11.104 3.47311 10.1817 3.06708C9.25943 2.66104 8.25709 2.46937 7.25006 2.50647C6.24304 2.54358 5.25752 2.80849 4.36761 3.28129C3.47771 3.7541 2.70656 4.42249 2.11215 5.23622C1.51774 6.04996 1.11554 6.98785 0.935783 7.9794C0.756025 8.97095 0.803388 9.99035 1.07431 10.961C1.34523 11.9316 1.83267 12.8281 2.49999 13.5833" stroke="white" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>{{ selectedFileName ? 'Upload now' : 'Import file' }}</span>
        </button>
        <span v-if="selectedFileName" class="selected-file-name">{{ selectedFileName }}</span>
      <!-- Accordion Sections -->
      <div class="accordion-section mt-4">
        <div class="accordion-card">
          <div class="accordion-header" @click="toggleAccordion('importFile')">
            <img src="@/assets/icons/import-modal-creatingImport.svg" alt="Creating Import" class="accordion-icon" />
            <span class="accordion-title">Creating an Import File</span>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="accordion-arrow" :class="{ open: accordionOpen.importFile }">
              <path d="M5 7.5L10 12.5L15 7.5" stroke="#667085" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div v-show="accordionOpen.importFile" class="accordion-body">
            <ol class="instructions-list">
              <li>
                Download the template File for example 
                <button type="button" class="download-template-btn" @click="downloadTemplate">
                  Download File
                </button>
              </li>
              <li>Fill the template with your specific monitor attributes (see below for explanation)</li>
            </ol>
          </div>
        </div>
        <div class="accordion-card mt-3">
          <div class="accordion-header" @click="toggleAccordion('fieldsDesc')">
            <img src="@/assets/icons/import-modal-description.svg" alt="Fields Description" class="accordion-icon" />
            <span class="accordion-title">Fields Description</span>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="accordion-arrow" :class="{ open: accordionOpen.fieldsDesc }">
              <path d="M5 7.5L10 12.5L15 7.5" stroke="#667085" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div v-show="accordionOpen.fieldsDesc" class="accordion-body">
            <ol class="fields-desc-list">
              <li><b>Type</b> - Categorizes the service being monitored.<br><span>Possible values: HTTP, KEYWORD, PING, PORT, HEARTBEAT, PAGE_SPEED, PAGE_SCREENSHOT</span></li>
              <li><b>Friendly Name</b> - A user-defined name for the service being monitored.</li>
              <li><b>URL/IP</b> - The URL or IP address of the service or device being monitored.</li>
              <li><b>Port</b> - The port number on which the service or device is running.</li>
              <li><b>Keyword Value</b> - A specific value or string that the monitoring system is checking for to determine proper functionality.</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MonitorsController from "../../services/monitorController";
import HelperFunction from "../../services/helperFunction";
import SampleCsv from "../../services/sampleCsvData";

export default {
  name: "ImportMonitor",
  props: {
    isOpen: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      selectedFileName: '',
      sampleDatafields: [],
      sampleData: [],
      accordionOpen: {
        importFile: true,
        fieldsDesc: false
      }
    };
  },
  mounted() {
    this.sampleDatafields = SampleCsv.sampleDatafields;
    this.sampleData = SampleCsv.sampleData;
  },
  methods: {
    validateCsvFile() {
      const file = this.$refs.csvFile.files[0];
      if (file) {
        const fileName = file.name;
        const fileExtension = fileName.split('.').pop();
        if (fileExtension.toLowerCase() !== 'csv') {
          this.$toasted.show('Please select only .csv file with defined fields in description.', {
            theme: 'outline',
            position: "top-center",
            type: 'error',
            duration: 5000
          });
          this.$refs.csvFile.value = '';
          this.selectedFileName = '';
        } else {
          this.selectedFileName = fileName;
          // Auto-trigger upload after file selection
          // this.uploadCsv();
        }
      }
    },
    
    async uploadCsv() {
      this.loading = true;
      if (this.$refs.csvFile.files[0]) {
        const file = this.$refs.csvFile.files[0];
        const formData = new FormData();
        formData.append('sampleFile', file);
        
        try {
          let response = await MonitorsController.csvUpload(formData);
          if (response.result) {
            this.$toasted.show(response.message, {
              theme: 'outline',
              position: "bottom-center",
              type: 'success',
              duration: 2000
            });
            this.$emit('upload-success');
            this.resetForm();
          } else {
            this.$toasted.show(response.message, {
              theme: 'outline',
              position: "bottom-center",
              type: 'error',
              duration: 2000
            });
          }
        } catch (error) {
          console.log(error);
          this.$toasted.show('Something went wrong!', {
            theme: 'outline',
            position: "bottom-center",
            type: 'error',
            duration: 2000
          });
        }
      } else {
        this.$toasted.show('Please select file', {
          theme: 'outline',
          position: "bottom-center",
          type: 'error',
          duration: 2000
        });
      }
      this.loading = false;
    },
    
    downloadTemplate() {
      const data = [this.sampleDatafields];
      const csvContent = "data:text/csv;charset=utf-8," + data.map(e => e.join(",")).join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "TemplateBulkImportFile.csv");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    
    downloadSampleData() {
      HelperFunction.downloadCSV(this.sampleData);
    },
    
    resetForm() {
      this.$refs.csvFile.value = '';
      this.selectedFileName = '';
    },
    toggleAccordion(section) {
      this.accordionOpen[section] = !this.accordionOpen[section];
    },
    triggerFileInput() {
      this.$refs.csvFile.click();
    }
  }
};
</script>

<style scoped>
.import-monitor-container {
  background: #FFFFFF;
  width: 100%;
  height: 100%;
  position: relative;
  /* Remove border-radius, box-shadow, max-width, margin */
  border-radius: 0;
  box-shadow: none;
  max-width: none;
  margin: 0;
  display: flex;
  flex-direction: column;
}

/* Loading Overlay */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
  border-radius: 16px;
}

.loader-box {
  text-align: center;
}

.loading-text {
  margin-top: 16px;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  color: #344054;
  font-weight: 500;
}

.import-monitor-header {
  padding: 24px 0 0 0;
  border-bottom: none;
}

.import-monitor-title {
  font-family: 'Poppins', sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: #101828;
  text-align: center;
  margin-bottom: 32px;
}

.import-monitor-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
}

/* File Upload Section */
.file-upload-section {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 32px;
  padding: 24px;
  background: #ffffff;
  border-radius: 12px;
}

.file-input-wrapper {
  position: relative;
  flex: 1;
}

.file-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.file-input-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: #FFFFFF;
  border: 1px solid #D0D5DD;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  color: #344054;
  cursor: pointer;
  transition: all 0.2s;
}

.file-input-label:hover {
  background: #F9FAFB;
  border-color: #7F56D9;
}

.upload-icon {
  width: 20px;
  height: 20px;
}

.upload-btn-icon {
  width: 20px;
  height: 20px;
}

/* Upload text styles */
.import-upload-title {
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #101828;
  margin-bottom: 4px;
}

.import-upload-desc {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 400;
  color: #667085;
}

.selected-file-name {
  display: block;
  margin-top: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #667085;
}

.upload-btn {
  padding: 12px 24px;
  background: #7F56D9;
  border: none;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #FFFFFF;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.upload-btn:hover:not(:disabled) {
  background: #6941C6;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(105, 65, 198, 0.3);
}

.upload-btn:disabled {
  background: #E5E7EB;
  color: #9CA3AF;
  cursor: not-allowed;
}

/* Instructions Section */
.instructions-section {
  margin-bottom: 32px;
}

.section-title {
  font-family: 'Poppins', sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: #161616;
  margin-bottom: 16px;
}

.instructions-list {
  margin: 0;
  padding-left: 20px;
}

.instructions-list li {
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #344054;
  margin-bottom: 12px;
  line-height: 20px;
}

.instructions-list li {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  color: #344054;
  margin-bottom: 8px;
}

.download-link {
  color: #7F56D9;
  text-decoration: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: color 0.2s;
}

.download-link:hover {
  color: #6941C6;
  text-decoration: underline;
}

/* Fields Section */
.fields-section {
  margin-bottom: 32px;
}

.fields-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.field-item {
  padding: 16px;
  background: #F9FAFB;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
}

.field-name {
  font-weight: 600;
  color: #161616;
  margin-right: 8px;
}

.field-desc {
  color: #344054;
  margin-right: 8px;
}

.field-values {
  color: #DC2626;
  font-weight: 500;
  font-size: 14px;
}

/* Sample Section */
.sample-section {
  text-align: center;
  padding-top: 16px;
  border-top: 1px solid #E5E7EB;
}

.download-sample-btn {
  padding: 12px 24px;
  background: #FFFFFF;
  border: 1px solid #D0D5DD;
  border-radius: 12px;
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 500;
  color: #344054;
  cursor: pointer;
  transition: all 0.2s;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.download-sample-btn:hover {
  background: #F9FAFB;
  border-color: #7F56D9;
  color: #7F56D9;
}

/* Accordion Sections */
.accordion-section {
  margin-top: 24px;
  width: 100% !important;
}
.accordion-card {
  background: transparent;
  border: 1px solid #D2E3FB;
  border-radius: 12px;
  box-shadow: none;
  margin-bottom: 12px;
  overflow: hidden;
}
.accordion-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  cursor: pointer;
  background: transparent;
  transition: background 0.2s;
}
.accordion-icon {
  width: 25px;
  height: 25px;
  margin-right: 12px;
  flex-shrink: 0;
}
.accordion-title {
  flex: 1;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 500;
  color: #344054;
}

.accordion-arrow {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  transition: transform 0.2s;
}
.accordion-arrow.open {
  transform: rotate(180deg);
}
.accordion-body {
  padding: 16px 20px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  color: #344054;
  background: transparent;
}
.download-template-btn {
  background: rgba(125, 134, 153, 1);
  color: #FFFFFF;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  padding: 8px 16px;
  margin-left: 8px;
  cursor: pointer;
  transition: background 0.2s;
  display: inline-block;
}
.download-template-btn:hover {
  background: #475467;
}
.fields-desc-list {
  margin: 0;
  padding-left: 20px;
}
.fields-desc-list li {
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  color: #344054;
  margin-bottom: 12px;
  line-height: 20px;
}

/* Responsive */
@media (max-width: 768px) {
  .file-upload-section {
    flex-direction: column;
    align-items: stretch;
  }
  
  .field-item {
    font-size: 14px;
  }
  
  .field-values {
    display: block;
    margin-top: 4px;
    font-size: 12px;
  }
}
.upload-now-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  margin: 24px 30px 0 30px;
  padding: 16px 24px;
  background: rgba(47, 178, 52, 1);
  color: #FFFFFF;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05);
}
.upload-now-btn:hover {
  background: #16A34A;
  box-shadow: 0px 1px 2px rgba(16, 24, 40, 0.05), 0px 0px 0px 4px #D1FAE5;
}
.file-input {
  display: none;
}
</style>